// 时间格式化工具函数
// 被所有需要显示时间的组件调用
// 作用：统一时间格式，转换为本地时间，保留到秒

import { logger } from "./logger";

/**
 * 格式化日期时间为本地时间格式
 * @param dateTimeString 时间字符串（ISO格式或其他格式）
 * @returns 格式化后的时间字符串，格式：YYYY-MM-DD HH:mm:ss
 */
export const formatDateTime = (
  dateTimeString: string | null | undefined
): string => {
  if (!dateTimeString) return "未知";

  try {
    // 后端返回的时间格式如：2025-07-26T02:05:09.003552
    // 这种格式没有时区信息，我们假设是UTC时间
    let date: Date;

    if (
      dateTimeString.includes("T") &&
      !dateTimeString.includes("Z") &&
      !dateTimeString.includes("+")
    ) {
      // 没有时区信息，假设是UTC时间，添加Z
      date = new Date(dateTimeString + "Z");
    } else if (dateTimeString.includes("Z")) {
      // 明确是UTC时间
      date = new Date(dateTimeString);
    } else if (dateTimeString.includes("+")) {
      // 有时区偏移，直接解析
      date = new Date(dateTimeString);
    } else {
      // 其他情况，假设是UTC时间
      date = new Date(dateTimeString + "Z");
    }

    if (isNaN(date.getTime())) return "无效时间";

    // 获取本地时间的各个部分
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");

    // 返回本地时间格式：YYYY-MM-DD HH:mm:ss
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  } catch (error) {
    logger.error("时间格式化错误:", error);
    return "时间错误";
  }
};

/**
 * 格式化日期（不包含时间）
 * @param dateString 日期字符串
 * @returns 格式化后的日期字符串，格式：YYYY-MM-DD
 */
export const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return "未知";

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "无效日期";

    return date.toLocaleDateString("zh-CN", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  } catch (error) {
    logger.error("日期格式化错误:", error);
    return "日期错误";
  }
};

/**
 * 格式化时间（不包含日期）
 * @param timeString 时间字符串
 * @returns 格式化后的时间字符串，格式：HH:mm:ss
 */
export const formatTime = (timeString: string | null | undefined): string => {
  if (!timeString) return "未知";

  try {
    const date = new Date(timeString);
    if (isNaN(date.getTime())) return "无效时间";

    return date.toLocaleTimeString("zh-CN", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  } catch (error) {
    logger.error("时间格式化错误:", error);
    return "时间错误";
  }
};

/**
 * 获取相对时间（如：3分钟前、1小时前等）
 * @param dateTimeString 时间字符串
 * @returns 相对时间字符串
 */
export const getRelativeTime = (
  dateTimeString: string | null | undefined
): string => {
  if (!dateTimeString) return "未知";

  try {
    const date = new Date(dateTimeString);
    if (isNaN(date.getTime())) return "无效时间";

    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) {
      return "刚刚";
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes}分钟前`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours}小时前`;
    } else {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days}天前`;
    }
  } catch (error) {
    logger.error("相对时间计算错误:", error);
    return "时间错误";
  }
};
